import { alias } from '@ember/object/computed';

import { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';

import Model from 'umg-ember-backend/models/variant-model';
import { saveHasManyRelationships } from 'umg-ember-backend/utils/model-save-hooks';
import {
  buildValidations,
  disabledWhenVariant,
  validator,
} from 'umg-ember-backend/utils/validations';

const Validations = buildValidations({
  categoryId: [
    validator('belongs-to'),
    validator('presence', {
      presence: true,
      disabled: disabledWhenVariant(),
    }),
  ],
  dvdDuration: validator('number', {
    allowString: true,
  }),
  headline: validator('presence', true),
  customShops: validator('has-many'),
  externalValues: validator('has-many'),
});

export const settings = {
  copyableOptions: {
    ignoreAttributes: [
      'channelsAdditive',
      'channelsExcluded',
      'channelsExclusive',
      'ownedByChannel',
    ],
    relationsToCopy: ['contributors', 'externalValues', 'customShops'],
  },
  saveHooks: {
    after: [
      saveHasManyRelationships('contributors', 'customShops', 'externalValues'),
    ],
  },
  variantProperties: [
    'cmsLanguageId',
    'seoTitle',
    'seoDescription',
    'keywords',
    'seoImage',
    'cmsState',
    'customShops',
    'headline',
    'mmcProductId',
    'productPhysId',
    'subheadline',
    'teaserHeadline',
    'teaserSubHeadline',
    'teaserText',
    'text',
  ],
};

export default class ProductModel extends Model.extend(Validations) {
  @attr('number') alphaInformationCarrierNumber;
  @attr('string') alphaInformationProductOutfitNotes;
  @attr('array') alphaInformationProductOutfits;
  @attr('string') alphaInformationVersion;
  @attr('boolean') compilation;
  @attr('string') country;
  @attr('boolean') coverEmbargo;
  @attr('string') dvdBonus;
  @belongsTo('category', { async: true, inverse: null }) dvdCountryCode;
  @attr('number') dvdDuration;
  @belongsTo('category', { async: true, inverse: null }) dvdRatio;
  @belongsTo('category', { async: true, inverse: null }) dvdSystem;
  @belongsTo('category', { async: true, inverse: null }) dvdFsk;
  @attr('string') entitySubtype;
  @attr('string') headline;
  @attr('boolean') hideTracks;
  @attr internationalReleaseDate;
  @attr('boolean') placedPrioritized;
  @attr('string') prizeDe;
  @attr('number', { defaultValue: 0 }) mmcProductId;
  @attr('number') productPhysId;
  @attr releaseDate;
  @attr('string') sku;
  @attr('boolean') soundEmbargo;
  @attr('string') subheadline;
  @attr('string') teaserHeadline;
  @attr('string') teaserSubHeadline;
  @attr('string') teaserText;
  @attr('string') text;
  @attr('boolean') trackEmbargo;
  @attr('boolean') useProdElemConf;
  @attr voeDate;
  @attr('boolean') voeTba;

  // CMS fields
  @attr('string') seoTitle;
  @attr('string') seoDescription;
  @attr('string') keywords;
  @attr('number', { defaultValue: 2 }) cmsState;

  // Meta fields
  @attr cmsCreated;
  @attr cmsDeleted;
  @attr cmsLocktime;
  @attr cmsModified;
  @attr() cmsHistory;
  @belongsTo('user', { async: true, inverse: null }) cmsOwnerId;
  @belongsTo('user', { async: true, inverse: null }) cmsCreatorId;
  @belongsTo('user', { async: true, inverse: null }) cmsLockerId;
  @belongsTo('user', { async: true, inverse: null }) cmsModifierId;

  // Relations
  @hasMany('category', { async: true, inverse: null }) additionalCategories;
  @hasMany('article', { async: true, inverse: null }) articles;
  @belongsTo('asset', { async: true, inverse: null }) assetId;
  @belongsTo('asset', { async: true, inverse: null }) assetId1;
  @belongsTo('asset', { async: true, inverse: null }) seoImage;
  @hasMany('category', { async: true, inverse: null }) categories;
  @belongsTo('category', { async: true, inverse: null }) categoryId;
  @belongsTo('category', { async: true, inverse: null }) categoryId1;
  @belongsTo('category', { async: true, inverse: null }) categoryId2;
  @belongsTo('category', { async: true, inverse: null }) categoryId3;
  @belongsTo('category', { async: true, inverse: null }) categoryId4;
  @hasMany('channel', { async: true, inverse: null }) channelsAdditive;
  @hasMany('channel', { async: true, inverse: null }) channelsExcluded;
  @hasMany('channel', { async: true, inverse: null }) channelsExclusive;
  @hasMany('product-contributor', { async: true, inverse: 'product' })
  contributors;
  @hasMany('custom-shop', { async: true, inverse: 'product' }) customShops;
  @hasMany('external-value', { async: true, inverse: 'product' })
  externalValues;
  @belongsTo('label', { async: true, inverse: null }) internationalLabel;
  @belongsTo('label', { async: true, inverse: null }) label;
  @belongsTo('channel', { async: true, inverse: null }) ownedByChannel;
  @belongsTo('category', { async: true, inverse: null }) productPhysCategoryId1;
  @hasMany('product-element', { async: true, inverse: null }) productElements;
  @hasMany('product', { async: true, inverse: null }) recommendedProducts;
  @hasMany('product-track', { async: true, inverse: null }) recommendedTracks;
  @hasMany('short-url', { async: true, inverse: null }) shortUrls;
  @hasMany('taxon', { async: true, inverse: null }) taxa;
  @hasMany('asset', { async: true, inverse: null }) videos;

  // Getter, setter and computed properties
  @alias('headline') displayHeadline;

  get displayLabel() {
    return `${this.headline} (${this.id})`;
  }

  get showAlphaInformation() {
    return (
      this.alphaInformationCarrierNumber ||
      this.alphaInformationProductOutfitNotes ||
      this.alphaInformationProductOutfits?.length > 0 ||
      this.alphaInformationVersion
    );
  }

  get showInternationalReleaseDate() {
    return this.internationalReleaseDate !== null;
  }
}
